import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert, Badge, Form } from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { feedbackLawyerConsultationApiService } from '../../services/apiServices';
import { FeedbackApplicationResponse, FeedbackApplicationStatus } from '../../api-client';
import { getFeedbackApplicationStatusLabel } from '../utils/statusConverters';

const FeedbackLawyerConsultationDetailPage: React.FC = () => {
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();

  const [application, setApplication] = useState<FeedbackApplicationResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState<boolean>(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  useEffect(() => {
    if (!applicationId) {
      navigate('/feedback/lawyer-consultation');
      return;
    }

    const loadApplication = async () => {
      try {
        setLoading(true);
        const response = await feedbackLawyerConsultationApiService.getApplication(applicationId);
        setApplication(response.data || null);
      } catch (err: any) {
        console.error('Ошибка при загрузке заявки:', err);
        setError(err.message || 'Ошибка при загрузке заявки');
      } finally {
        setLoading(false);
      }
    };

    loadApplication();
  }, [applicationId, navigate]);

  const handleStatusUpdate = async (newStatus: FeedbackApplicationStatus) => {
    if (!applicationId) return;

    try {
      setUpdating(true);
      setUpdateError(null);
      await feedbackLawyerConsultationApiService.updateStatus(applicationId, newStatus);

      // Обновляем локальное состояние
      setApplication((prev) => (prev ? { ...prev, status: newStatus } : null));
    } catch (err: any) {
      console.error('Ошибка при обновлении статуса:', err);
      setUpdateError(err.message || 'Ошибка при обновлении статуса');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadgeVariant = (status: FeedbackApplicationStatus) => {
    switch (status) {
      case FeedbackApplicationStatus.PENDING:
        return 'warning';
      case FeedbackApplicationStatus.SCHEDULED:
        return 'info';
      case FeedbackApplicationStatus.COMPLETED:
        return 'success';
      case FeedbackApplicationStatus.CANCELED:
        return 'danger';
      default:
        return 'secondary';
    }
  };

  if (loading) {
    return (
      <Container
        fluid
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '50vh' }}
      >
        <div className="text-center">
          <Spinner animation="border" />
          <p className="mt-2">Загрузка заявки...</p>
        </div>
      </Container>
    );
  }

  if (error || !application) {
    return (
      <Container fluid>
        <Row className="mt-4">
          <Col>
            <Alert variant="danger">{error || 'Заявка не найдена'}</Alert>
            <Button variant="primary" onClick={() => navigate('/feedback/lawyer-consultation')}>
              Вернуться к списку
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row className="mt-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Заявка №{application.id}</h2>
            <Link to="/feedback/lawyer-consultation">
              <Button variant="outline-secondary">Вернуться к списку</Button>
            </Link>
          </div>

          {updateError && <Alert variant="danger">{updateError}</Alert>}

          <Card>
            <Card.Header>
              <Badge bg={getStatusBadgeVariant(application.status)}>
                {getFeedbackApplicationStatusLabel(application.status)}
              </Badge>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <h5>Основная информация</h5>
                  <table className="table table-bordered">
                    <tbody>
                      <tr>
                        <th style={{ width: '40%' }}>ID заявки</th>
                        <td>{application.id}</td>
                      </tr>
                      <tr>
                        <th>Пользователь</th>
                        <td>
                          {application.user?.profile?.full_name && (
                            <div>
                              <strong>{application.user.profile.full_name}</strong>
                            </div>
                          )}
                          <div>
                            <strong>{application.user?.username || '—'}</strong>
                          </div>
                          <small className="text-muted">{application.user?.email || '—'}</small>
                        </td>
                      </tr>
                      <tr>
                        <th>Тип</th>
                        <td>
                          <Badge bg="primary">
                            {application.type === 'hr_one_on_one'
                              ? 'HR 1-on-1'
                              : 'Консультация юриста'}
                          </Badge>
                        </td>
                      </tr>
                      <tr>
                        <th>Статус</th>
                        <td>
                          <Badge bg={getStatusBadgeVariant(application.status)}>
                            {getFeedbackApplicationStatusLabel(application.status)}
                          </Badge>
                        </td>
                      </tr>
                      <tr>
                        <th>Дата создания</th>
                        <td>{new Date(application.created_at).toLocaleString()}</td>
                      </tr>
                    </tbody>
                  </table>
                </Col>

                <Col md={6}>
                  <h5>Описание</h5>
                  <p className="text-muted">{application.description || 'Описание отсутствует'}</p>

                  <hr />

                  <h5>Обновление статуса</h5>
                  <Form>
                    <Form.Group className="mb-3">
                      <Form.Label>Установить статус:</Form.Label>
                      <div className="d-grid gap-2">
                        <Button
                          variant={
                            application.status === FeedbackApplicationStatus.PENDING
                              ? 'warning'
                              : 'outline-warning'
                          }
                          onClick={() => handleStatusUpdate(FeedbackApplicationStatus.PENDING)}
                          disabled={
                            updating || application.status === FeedbackApplicationStatus.PENDING
                          }
                          size="sm"
                        >
                          {application.status === FeedbackApplicationStatus.PENDING ? '✓ ' : ''}
                          Ожидает обработки
                        </Button>
                        <Button
                          variant={
                            application.status === FeedbackApplicationStatus.SCHEDULED
                              ? 'info'
                              : 'outline-info'
                          }
                          onClick={() => handleStatusUpdate(FeedbackApplicationStatus.SCHEDULED)}
                          disabled={
                            updating || application.status === FeedbackApplicationStatus.SCHEDULED
                          }
                          size="sm"
                        >
                          {application.status === FeedbackApplicationStatus.SCHEDULED ? '✓ ' : ''}
                          Запланировано
                        </Button>
                        <Button
                          variant={
                            application.status === FeedbackApplicationStatus.COMPLETED
                              ? 'success'
                              : 'outline-success'
                          }
                          onClick={() => handleStatusUpdate(FeedbackApplicationStatus.COMPLETED)}
                          disabled={
                            updating || application.status === FeedbackApplicationStatus.COMPLETED
                          }
                          size="sm"
                        >
                          {application.status === FeedbackApplicationStatus.COMPLETED ? '✓ ' : ''}
                          Завершено
                        </Button>
                        <Button
                          variant={
                            application.status === FeedbackApplicationStatus.CANCELED
                              ? 'danger'
                              : 'outline-danger'
                          }
                          onClick={() => handleStatusUpdate(FeedbackApplicationStatus.CANCELED)}
                          disabled={
                            updating || application.status === FeedbackApplicationStatus.CANCELED
                          }
                          size="sm"
                        >
                          {application.status === FeedbackApplicationStatus.CANCELED ? '✓ ' : ''}
                          Отменено
                        </Button>
                      </div>
                    </Form.Group>
                  </Form>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default FeedbackLawyerConsultationDetailPage;
